/**
 * Fine-grained actions (GET /me/actions) beside the screen policies
 * (GET /me/policies).
 *
 * The server lists a module in `enforcedModules` once its wave is on; nothing
 * here changes per wave. To move a module's UI over:
 * - Menus and routes: give the routeConfig item `actions` next to its
 *   `policies`, which stay. Until the module is enforced the policies decide;
 *   after, any one of the actions opens the item, so a grant with no screen
 *   policy behind it opens it too.
 * - Buttons: `useActionGate(action, legacyCheck)`, with today's check as
 *   `legacyCheck`, or none where there was none (see useCanReversePayroll).
 * - Screens that exist only under IAM: `useCan(action)`, since their endpoints
 *   check the action in every mode.
 * When /me/actions fails twice in a row, no module counts as enforced and no
 * action as held: everything goes by the screen policies, as before any wave,
 * and whatever `useCan` gates stays hidden.
 */
import { ENFORCED_MODULES, ME_ACTIONS } from '@nxsys/store/constants/keys';

/** What GET /me/actions says about the signed-in user. */
export type ActionAccess = {
  actions: string[];
  enforcedModules: string[];
};

export const NO_ACTION_ACCESS: ActionAccess = {
  actions: [],
  enforcedModules: [],
};

/** `payroll:reversePayslip` → `payroll` */
export const moduleOfAction = (action: string): string => action.split(':')[0];

/**
 * Whether something that declares fine-grained actions is open to the user.
 *
 * An action is checked against /me/actions only once its module is enforced.
 * Until then `legacyAllowed` (today's check on the screen policies) answers for
 * it, so a module's menus, routes and buttons behave exactly as before its
 * wave.
 * Any one declared action is enough.
 */
export const canPerform = (
  access: ActionAccess,
  declared: string | string[],
  legacyAllowed: boolean,
): boolean => {
  const list = Array.isArray(declared) ? declared : [declared];
  if (list.length === 0) return legacyAllowed;
  return list.some((action) =>
    access.enforcedModules.includes(moduleOfAction(action))
      ? access.actions.includes(action)
      : legacyAllowed,
  );
};

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

/** The `data` of GET /me/actions. Anything malformed reads as no access. */
export const toActionAccess = (data: unknown): ActionAccess => {
  const body = (data ?? {}) as Record<string, unknown>;
  return {
    actions: strings(body.actions),
    enforcedModules: strings(body.enforcedModules),
  };
};

export const readStoredActionAccess = (): ActionAccess => {
  try {
    return {
      actions: strings(JSON.parse(localStorage.getItem(ME_ACTIONS) || '[]')),
      enforcedModules: strings(
        JSON.parse(localStorage.getItem(ENFORCED_MODULES) || '[]'),
      ),
    };
  } catch {
    return NO_ACTION_ACCESS;
  }
};
